import * as React from 'react'
import { createPortal } from 'react-dom'
import styled, { useTheme } from 'styled-components'

export const Step = styled.div<{ $focused: boolean }>`
  background-color: white;
  cursor: pointer;

  ${props => props.theme.castingPluginLayout === 'row' && `
    max-width: 25em;
    flex-grow: 0.001;

    @media (min-width: ${props.theme.breakpoints.m}px) {
      ${ props.$focused && 'flex-grow: 10; transition: all 0.5s ease-out;' }
    }

    @media (min-width: ${props.theme.breakpoints.l}px) {
      min-width: fit-content;
    }

    @media (max-width: ${props.theme.breakpoints.m - 1}px) {
      max-width: unset;
      width: 100%;
    }

    @media (min-width: ${props.theme.breakpoints.m}px) and (max-width: ${props.theme.breakpoints.l - 1}px) {
      max-width: unset;
      width: 50%;
    }
  `}

  ${props => props.theme.castingPluginLayout === 'two_columns' && `
    &:not(:last-of-type) {
      border-bottom: 1px solid ${ props.theme.colors.clr_0_lighten };
    }
  `}
`

export const StepContent = styled.div<{ $focused: boolean }>`
  margin: 1em 0;
  padding: 0 1.5em;

  ${props => props.theme.castingPluginLayout === 'row' && `
    @media (min-width: ${props.theme.breakpoints.m}px) {
      ${Step}:not(:last-of-type) & {
        border-right: 1px solid ${ props.theme.colors.clr_0_lighten };
        margin-right: -1px;
        z-index: 1;
        position: relative;
      }
    }

    ${ props.$focused && `border-right: 1px solid ${ props.theme.colors.clr_0_lighten };` }
  `}
`

export const StepTitle = styled.div`
  text-transform: uppercase;
  color: ${ props => props.theme.colors.clr_1 };
  font-weight: bold;
  margin-bottom: 0.2em;
`

export const StepSummary = styled.div`
  color: #565656;
  font-weight: bold;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
`

export const StepProgress = styled.div<{ $percentage: number }>`
  width: ${ props => props.$percentage }%;
  height: 0.5em;
  background-color: ${ props => props.theme.colors.clr_1 };
  transition: width 0.5s ease-out;
`

const StepDeployedContainerInner = styled.div<{ $fullscreenBreakpoint?: number }>`
  @media (max-width: ${props => props.$fullscreenBreakpoint || props.theme.breakpoints.m}px) {
    position: fixed !important;
    z-index: 100000;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    overflow: auto;
  }

  ${props => props.theme.castingPluginLayout === 'row' && `
    position: absolute;
    z-index: 100000;
    background-color: white;
    box-shadow: 0 1px 3px rgb(0 0 0 / 12%), 0 1px 2px rgb(0 0 0 / 24%);

    @media (min-width: ${props.theme.breakpoints.m}px) {
      width: 25em;
    }
  `}
`

export function StepDeployedContainer (props: { fullscreenBreakpoint?: number } & React.HTMLAttributes<HTMLDivElement>) {
  const theme = useTheme()
  const elementRef = React.useRef<HTMLDivElement>(null)
  const [outOfScreenLeft, setOutOfScreenLeft] = React.useState(false)
  const [outOfScreenRight, setOutOfScreenRight] = React.useState(false)

  const childStyle: React.CSSProperties = outOfScreenLeft
    ? { right: 0 }
    : outOfScreenRight
    ? { left: 0 }
    : theme.castingPluginRowLayoutAnchor?.scrollLeft
    ? { transform: `translateX(-${theme.castingPluginRowLayoutAnchor.scrollLeft}px)` }
    : {}

  // In a portal, it is easier to be over any other element, which is required for mobile layout.
  // The portal is also used in the 2 columns layout, to be anchored inside the right column.
  const portalAnchor = window.innerWidth < theme.breakpoints.m ? window.document.body : theme.castingPluginDeployedStepAnchor
  const child = <StepDeployedContainerInner className="oss oss-step-deployed-container-inner" { ...props } style={ childStyle } />

  // eslint-disable-next-line react-hooks/exhaustive-deps -- we check if the element is out of screen on every render (so no dependency is given on purpose)
  React.useLayoutEffect(() => {
    if (portalAnchor || !elementRef.current) {
      if (outOfScreenLeft) setOutOfScreenLeft(false)
      if (outOfScreenRight) setOutOfScreenRight(false)
      return
    }

    const rect = elementRef.current.getBoundingClientRect()
    const newOutOfScreenLeft = rect.left + 25 * 14 > window.innerWidth
    if (newOutOfScreenLeft !== outOfScreenLeft) setOutOfScreenLeft(newOutOfScreenLeft)

    const newOutOfScreenRight = rect.left < 0
    if (newOutOfScreenRight !== outOfScreenRight) setOutOfScreenRight(newOutOfScreenRight)
  })

  return <div ref={ elementRef }>
    { portalAnchor === window.document.body && <style>{ `@media (max-width: ${theme.breakpoints.m}px) { body { overflow: hidden; } }` }</style> }
    { portalAnchor ? createPortal(child, portalAnchor) : child }
  </div>
}

export const Input = styled.input<{ $hasErrors?: boolean }>`
  ${ props => !props.style?.padding && 'padding: 0.875em !important;' }
  box-sizing: border-box;
  width: 100%;
  border-radius: 4px;
  border: 1px solid ${ props => props.$hasErrors ? '#e09c95' : props.theme.colors.clr_0 };
  outline: none;

  ${ props => props.$hasErrors && `
    color: #c0392b;
    background-color: #fcf5f4;
  ` }
`

export const DeployedFloatingInputCardContent = styled.div`
  padding: 0.5em;
`

export const NoResult = styled.div`
  margin: 0.5em;
  background: #f8fafb;
  padding: 2em;
  line-height: 1.4285em;
  color: #718491;
  text-align: center;
  border-radius: .125rem;
  box-shadow: 0 0 0 1px #e9eef1;
`

export const TooltipHeader = styled.div<{ $force?: boolean }>`
  font-weight: bold;
  text-align: center;
  background: #e9eef1;
  margin: 0;
  color: #384248;
  position: relative;
  height: 3em;

  ${ props => !props.$force && props.theme.castingPluginLayout === 'row' && `@media (min-width: ${props.theme.breakpoints.m}px) {
    display: none;
  }` }
`

export const TooltipHeaderTitle = styled.div`
  position: absolute;
  top: 1em;
  left: 2.5em;
  text-overflow: ellipsis;
  width: calc(100% - 5em);
  display: block;
  overflow: hidden;
  white-space: nowrap;
`

export const DescriptionContainer = styled.div`
  background-color: ${props => props.theme.colors.clr_0_lightest};
  padding: 0;
  ${ props => props.theme.roundCornerInsideTooltips && 'border-radius: 0.25em' }
`

export const DescriptionTab = styled.a<{ $active: boolean }>`
  border-bottom: 1px solid ${props => props.theme.colors.clr_0_lighten};
  width: 50%;
  text-align: center;
  font-size: 80%;
  font-weight: bold;
  padding: 0.75em;
  line-height: 1.5em;
  cursor: pointer;

  ${ props => !props.$active && `color: ${props.theme.colors.clr_0} !important;` }

  &:first-of-type {
    border-right: 1px solid ${props => props.theme.colors.clr_0_lighten};
  }
`

export const DescriptionParagraph = styled.div`
  padding: 0.75em;
  text-align: justify;
  font-size: 0.875rem;
  line-height: 1.4em;
`

export const InfoBox = styled.div`
  padding: .85714em 1.42857em 1em;
  background: ${ props => props.theme.colors.clr_0_light };
  text-align: center;
  color: white;
`
export const InfoBoxWarning = styled(InfoBox)`
  background: ${ props => props.theme.colors.clr_warning_darker };
`

export const InfoBoxError = styled(InfoBox)`
  background: ${ props => props.theme.colors.clr_error };
`

export const PrimaryButton = styled.a<{ $disabled?: boolean }>`
  background: ${ props => props.theme.colors.clr_1 };
  color: white !important;
  text-align: center;
  border-radius: 0.25em;
  cursor: pointer;
  padding: 0.8em;
  ${ props => props.$disabled ? 'opacity: 0.3;' : '' }
`

export const NeutralButton = styled(PrimaryButton)`
  color: ${ props => props.theme.colors.clr_0_light };
  background: #434f57 !important;
`

export const FloatingContainerTitle = styled.div`
  font-weight: bold;
  margin-bottom: 1.25rem;
  color: ${ props => props.theme.colors.clr_0_dark }
`

export const Link = styled.a`
  color: ${props => props.theme.colors.clr_1};
  fill: ${props => props.theme.colors.clr_1};
  cursor: pointer;
`

export const TwoColumnsLeft = styled.div`
  width: 25em;
  background-color: white;
  border-right: 1px solid ${props => props.theme.colors.clr_0_lighten};
`
